/**
 * Golden funnel dataLayer pushes (GA4 event names).
 * Merchant ad platforms fire from GTM. TrafiFlow platform pixel fires via tfp().
 */

import {
  buildBeginCheckoutEventScope,
  buildPurchaseEventId,
  createPixelEventId,
  publishPixelEventId,
  resolvePixelEventId,
} from './analytics-event-id';
import { getDataLayerContext } from './datalayer-context';
import { getVendorAnonIds } from './vendor-anon-ids';
import {
  normalizeEmailForHash,
  normalizePhoneForHash,
  sha256Hex,
} from './pii-hash';
import {
  isCheckoutAlreadyTracked,
  isPurchaseAlreadyTracked,
  markCheckoutTracked,
  markPurchaseTracked,
  isIdentityAlreadyTracked,
  markIdentityTracked,
  shouldSkipAddToCartDedup,
} from './analytics-dedup';
import {
  buildEcommerceItemFromCartLine,
  buildEcommerceItemFromOrderLine,
  buildEcommerceItemFromProductVariant,
  centsToEcommerceValue,
  sumEcommerceValueFromItems,
} from './ecommerce-mappers';
import type {
  CartLineLikeForAnalytics,
  EcommerceItem,
  OrderLineLikeForAnalytics,
  ProductLikeForAnalytics,
  VariantLikeForAnalytics,
} from './ecommerce-types';
import {
  tfPixelIdentify,
  tfPixelTrackAddToCart,
  tfPixelTrackCheckoutStarted,
  tfPixelTrackPurchase,
  tfPixelTrackViewContent,
} from './tf-pixel-bridge';
import {
  EVENT_TYPES,
  publishBrowserBusinessEvent,
} from './business-event-bridge';

export type { EcommerceItem } from './ecommerce-types';

const TRACKED_ORDER_KEY = 'te_last_tracked_order_id';
const TRACKED_CHECKOUT_KEY = 'te_last_begin_checkout_token';

export interface VisitorIdentityParams {
  email?: string | null;
  phone?: string | null;
}

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

function pushDataLayer(payload: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

function pushEcommerceEvent(
  event: string,
  ecommerce: Record<string, unknown>,
  eventId: string,
): void {
  publishPixelEventId(eventId);
  pushDataLayer({ ecommerce: null });
  pushDataLayer({
    event,
    event_id: eventId,
    ...getDataLayerContext(),
    ecommerce,
  });
}

function pushCustomEvent(event: string, payload: Record<string, unknown>, eventId: string): void {
  publishPixelEventId(eventId);
  pushDataLayer({
    event,
    event_id: eventId,
    ...getDataLayerContext(),
    ...payload,
  });
}

function cleanIdentityValue(value?: string | null): string | undefined {
  const trimmed = value?.trim();
  return trimmed || undefined;
}

/** Vendor-neutral identity signal. GTM destinations decide how to consume it. */
/** Every new email/phone capture surface MUST call this (see tracking-strategy-client R13). */
export function trackVisitorIdentity(params: VisitorIdentityParams): void {
  void pushVisitorIdentity(params);
}

async function pushVisitorIdentity(params: VisitorIdentityParams): Promise<void> {
  const email = cleanIdentityValue(params.email)?.toLowerCase();
  const phone = cleanIdentityValue(params.phone);

  if (!email && !phone) return;

  const identityKey = email ?? phone!;
  if (isIdentityAlreadyTracked(identityKey)) return;
  markIdentityTracked(identityKey);

  const [emailSha256, phoneSha256] = await Promise.all([
    email ? sha256Hex(normalizeEmailForHash(email)) : Promise.resolve(undefined),
    phone ? sha256Hex(normalizePhoneForHash(phone)) : Promise.resolve(undefined),
  ]);

  const identity = {
    ...(emailSha256 ? { email_sha256: emailSha256 } : {}),
    ...(phoneSha256 ? { phone_sha256: phoneSha256 } : {}),
    ...getVendorAnonIds(),
  };

  const eventId = createPixelEventId();

  pushCustomEvent('identify_user', { identity }, eventId);

  tfPixelIdentify({ email, eventId, phone });
}

/** GA4 view_item — PDP / product embed. */
export function trackViewContent(params: {
  product: ProductLikeForAnalytics;
  variant: VariantLikeForAnalytics;
  currency: string;
  quantity?: number;
  itemListName?: string;
}): string {
  const eventId = createPixelEventId();
  const items = [
    buildEcommerceItemFromProductVariant(params.product, params.variant, params.quantity ?? 1, {
      itemListName: params.itemListName ?? 'product_detail',
      itemListId: params.product.slug,
    }),
  ];
  pushEcommerceEvent(
    'view_item',
    {
      currency: params.currency,
      value: sumEcommerceValueFromItems(items),
      items,
    },
    eventId,
  );

  tfPixelTrackViewContent({
    currency: params.currency,
    eventId,
    items,
    value: sumEcommerceValueFromItems(items),
  });

  publishBrowserBusinessEvent(
    EVENT_TYPES.PRODUCT_VIEWED,
    {
      product_id: params.product.uuid,
      product_name: params.product.title,
      product_sku: params.variant.sku ?? null,
      category: params.product.tags?.[0] ?? null,
      price_cents: Math.round(params.variant.price.display),
      currency: params.currency,
    },
    eventId,
  );
  return eventId;
}

/** GA4 add_to_cart */
export function trackAddToCart(params: {
  product: ProductLikeForAnalytics;
  variant: VariantLikeForAnalytics;
  quantity: number;
  currency: string;
  itemListName?: string;
  cartId?: string | null;
}): string | null {
  if (shouldSkipAddToCartDedup(params.product.uuid, params.variant.id, params.quantity)) {
    return null;
  }

  const eventId = createPixelEventId();
  const items = [
    buildEcommerceItemFromProductVariant(params.product, params.variant, params.quantity, {
      itemListName: params.itemListName ?? 'product_detail',
      itemListId: params.product.slug,
    }),
  ];
  pushEcommerceEvent(
    'add_to_cart',
    {
      currency: params.currency,
      value: sumEcommerceValueFromItems(items),
      items,
    },
    eventId,
  );

  tfPixelTrackAddToCart({
    currency: params.currency,
    eventId,
    items,
    value: sumEcommerceValueFromItems(items),
  });

  publishBrowserBusinessEvent(
    EVENT_TYPES.PRODUCT_ADDED_TO_CART,
    {
      product_id: params.product.uuid,
      product_name: params.product.title,
      product_sku: params.variant.sku ?? null,
      quantity: params.quantity,
      price_cents: Math.round(params.variant.price.display),
      currency: params.currency,
      cart_id: params.cartId ?? null,
    },
    eventId,
  );
  return eventId;
}

/** GA4 add_to_cart from cart line shape (after API add when product entity unavailable). */
export function trackAddToCartFromLines(params: {
  currency: string;
  lines: CartLineLikeForAnalytics[];
  itemListName?: string;
  cartId?: string | null;
}): string {
  const eventId = createPixelEventId();
  const items = params.lines.map((line, index) =>
    buildEcommerceItemFromCartLine(line, {
      itemListName: params.itemListName ?? 'cart',
      index,
    }),
  );
  pushEcommerceEvent(
    'add_to_cart',
    {
      currency: params.currency,
      value: sumEcommerceValueFromItems(items),
      items,
    },
    eventId,
  );

  tfPixelTrackAddToCart({
    currency: params.currency,
    eventId,
    items,
    value: sumEcommerceValueFromItems(items),
  });

  params.lines.forEach((line, index) => {
    publishBrowserBusinessEvent(
      EVENT_TYPES.PRODUCT_ADDED_TO_CART,
      {
        product_id: line.product_id,
        product_name: line.name,
        product_sku: line.sku ?? null,
        quantity: line.quantity,
        price_cents:
          line.quantity > 0
            ? Math.round(line.price.display / line.quantity)
            : Math.round(line.price.display),
        currency: params.currency,
        cart_id: params.cartId ?? null,
      },
      index === 0 ? eventId : createPixelEventId(),
    );
  });
  return eventId;
}

/** GA4 begin_checkout — once per cart token per session. */
export function trackCheckoutStarted(params: {
  cartToken: string;
  currency: string;
  items: EcommerceItem[];
  value?: number;
  coupon?: string;
  shipping?: number;
  tax?: number;
}): string | null {
  if (typeof window === 'undefined') return null;

  const last = sessionStorage.getItem(TRACKED_CHECKOUT_KEY);
  if (last === params.cartToken || isCheckoutAlreadyTracked(params.cartToken)) {
    // The golden/canonical event already fired (possibly on the layout origin),
    // but funnel UI telemetry may still need to correlate its local observation.
    return resolvePixelEventId(buildBeginCheckoutEventScope(params.cartToken));
  }

  const value = params.value ?? sumEcommerceValueFromItems(params.items);
  const eventId = resolvePixelEventId(buildBeginCheckoutEventScope(params.cartToken));

  pushEcommerceEvent(
    'begin_checkout',
    {
      cart_token: params.cartToken,
      currency: params.currency,
      value,
      item_count: params.items.reduce((n, i) => n + (i.quantity ?? 1), 0),
      items: params.items,
      ...(params.coupon ? { coupon: params.coupon } : {}),
      ...(params.shipping != null ? { shipping: params.shipping } : {}),
      ...(params.tax != null ? { tax: params.tax } : {}),
    },
    eventId,
  );

  tfPixelTrackCheckoutStarted({
    cartToken: params.cartToken,
    coupon: params.coupon,
    currency: params.currency,
    eventId,
    itemCount: params.items.reduce((n, i) => n + (i.quantity ?? 1), 0),
    items: params.items,
    shipping: params.shipping,
    tax: params.tax,
    value,
  });

  publishBrowserBusinessEvent(
    EVENT_TYPES.CHECKOUT_STARTED,
    {
      cart_id: params.cartToken,
      item_count: params.items.reduce((n, item) => n + (item.quantity ?? 1), 0),
      total_cents: Math.round(value * 100),
      currency: params.currency,
    },
    eventId,
  );

  sessionStorage.setItem(TRACKED_CHECKOUT_KEY, params.cartToken);
  markCheckoutTracked(params.cartToken);
  return eventId;
}

export function trackCheckoutStartedFromCart(params: {
  cartToken: string;
  currency: string;
  lines: CartLineLikeForAnalytics[];
  coupon?: string;
  shippingCents?: number;
  taxCents?: number;
}): string | null {
  const items = params.lines.map((line, index) =>
    buildEcommerceItemFromCartLine(line, { itemListName: 'cart', index }),
  );
  return trackCheckoutStarted({
    cartToken: params.cartToken,
    currency: params.currency,
    items,
    coupon: params.coupon,
    shipping:
      params.shippingCents != null ? centsToEcommerceValue(params.shippingCents) : undefined,
    tax: params.taxCents != null ? centsToEcommerceValue(params.taxCents) : undefined,
  });
}

/** GA4 purchase — deduped by order id. */
export function trackPurchase(params: {
  orderId: string;
  currency?: string;
  value?: number;
  items?: EcommerceItem[];
  coupon?: string;
  shipping?: number;
  tax?: number;
  affiliation?: string;
}): string | null {
  if (typeof window === 'undefined') return null;

  const lastTracked = sessionStorage.getItem(TRACKED_ORDER_KEY);
  if (lastTracked === params.orderId || isPurchaseAlreadyTracked(params.orderId)) {
    return buildPurchaseEventId(params.orderId);
  }

  const items = params.items ?? [];
  const value = params.value ?? sumEcommerceValueFromItems(items);
  const eventId = buildPurchaseEventId(params.orderId);

  pushEcommerceEvent(
    'purchase',
    {
      transaction_id: params.orderId,
      value,
      currency: params.currency,
      items,
      ...(params.coupon ? { coupon: params.coupon } : {}),
      ...(params.shipping != null ? { shipping: params.shipping } : {}),
      ...(params.tax != null ? { tax: params.tax } : {}),
      ...(params.affiliation ? { affiliation: params.affiliation } : {}),
    },
    eventId,
  );

  tfPixelTrackPurchase({
    affiliation: params.affiliation,
    coupon: params.coupon,
    currency: params.currency,
    eventId,
    items,
    orderId: params.orderId,
    shipping: params.shipping,
    tax: params.tax,
    value,
  });

  sessionStorage.setItem(TRACKED_ORDER_KEY, params.orderId);
  markPurchaseTracked(params.orderId);
  return eventId;
}

export function trackPurchaseFromOrderLines(params: {
  orderId: string;
  currency: string;
  lines: OrderLineLikeForAnalytics[];
  totalCents?: number;
  coupon?: string;
  shippingCents?: number;
  taxCents?: number;
}): string | null {
  const items = params.lines.map((line, index) => buildEcommerceItemFromOrderLine(line, { index }));
  const value =
    params.totalCents !== undefined
      ? centsToEcommerceValue(params.totalCents)
      : sumEcommerceValueFromItems(items);

  return trackPurchase({
    orderId: params.orderId,
    currency: params.currency,
    value,
    items,
    coupon: params.coupon,
    shipping:
      params.shippingCents != null ? centsToEcommerceValue(params.shippingCents) : undefined,
    tax: params.taxCents != null ? centsToEcommerceValue(params.taxCents) : undefined,
  });
}
